import { Button, Form, Input, Modal, Row, Table, Typography } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';

import {
  useUpdateSalePlansMutation,
  type ModelGetSalePlan,
  type ModelSalePlan,
} from '@app/api/SalePlans';
import { CloseIconsForModalWithDataTestId } from '@app/components';
import { OFF, SUCCESS, WARNING } from '@app/constants';
import { useContentWidth, useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { divideAmount } from '@app/utils';

import { generateSalePlanRows } from '@pages/SalePlans/utils';

import {
  SP_CATEGORY,
  SP_TABLE_COLUMNS,
  SP_TOTAL,
} from '@pages/SalePlans/constants/spTableColumns';

import { useSalePlansPageTable } from '@pages/SalePlans/hooks';
import { saveOnlyNumbers } from '@pages/SalePlans/utils/saveOnlyNumbers';

import type { ColumnsType } from 'antd/es/table';
import type { FC } from 'react';

const { Text } = Typography;
const { useForm } = Form;

interface SalePlansTableProps {
  data: ModelGetSalePlan | null;
  newValue: number | string;
  year: number;
  loader: boolean;
  setNewValue: (value: number | string) => void;
  updateRegistry: () => Promise<void>;
}

export const SalePlansTable: FC<SalePlansTableProps> = ({
  data,
  year,
  loader,
  setNewValue,
  updateRegistry,
}) => {
  const { hasUserRightToCreateSalePlans } = useSalePlansPageTable();
  const { showNotification } = useContext(NotificationContext);
  const { activeBranchId } = useUserPrint();
  const [updateSalePlans] = useUpdateSalePlansMutation();
  const [editingCell, setEditingCell] = useState<ModelSalePlan | null>(null);
  const [isModal, setModal] = useState(false);
  const { contentWidth } = useContentWidth();
  const [form] = useForm();

  const handleEditCell = useCallback(
    (cellData: ModelSalePlan) => {
      form.setFieldValue(
        'newValue',
        Number(cellData.PlanMoney) === 0 ? null : cellData.PlanMoney
      );
      setModal(true);
    },
    [form]
  );

  const salePlanColumns: ColumnsType = useMemo(
    () =>
      SP_TABLE_COLUMNS.map(({ id: columnId, label }) => ({
        title: label,
        dataIndex: columnId,
        key: columnId,
        width: 'auto',
        ellipsis: false,
        render: (text, record, rowIndex) => {
          const separatedText = divideAmount(text);
          const handledText = columnId === SP_CATEGORY ? text : separatedText;
          const cellStyles = {
            display: 'flex',
            justifyContent: columnId === SP_CATEGORY ? 'start' : 'end',
          };
          const columnIndex = SP_TABLE_COLUMNS.find(
            ({ id }) => id === columnId
          )?.monthJsIndex;
          const monthKey =
            columnIndex === undefined
              ? ''
              : `${year}-${String(columnIndex + 1).padStart(2, '0')}`;

          if (
            rowIndex === 0 ||
            columnId === SP_CATEGORY ||
            columnId === SP_TOTAL
          ) {
            return columnId === SP_TOTAL ? (
              <div className="total-cell" style={cellStyles}>
                <Text strong>
                  {generateSpanWithDataTestId(
                    handledText || '-',
                    'overall-plan-value'
                  )}
                </Text>
              </div>
            ) : columnId !== SP_CATEGORY ? (
              <div className="total-cell" style={cellStyles}>
                <Text strong data-testid={`sale-plan-month-total-${monthKey}`}>
                  {generateSpanWithDataTestId(
                    handledText || '-',
                    'specified-plan-value'
                  )}
                </Text>
              </div>
            ) : (
              <div className="total-cell" style={cellStyles}>
                <Text strong>{handledText || '-'}</Text>
              </div>
            );
          } else {
            return (
              <div
                className="editable-cell"
                style={cellStyles}
                data-testid={`sale-plan-cell-${record.categoryId}-${monthKey}`}
                onClick={() => {
                  const cellData = record.planIds?.[String(columnIndex)];

                  setEditingCell(cellData);
                  setNewValue(cellData.PlanMoney);
                  hasUserRightToCreateSalePlans
                    ? handleEditCell(cellData)
                    : setModal(false);
                }}
              >
                <Text>
                  {generateSpanWithDataTestId(
                    separatedText,
                    'specified-plan-value'
                  )}
                </Text>
              </div>
            );
          }
        },
      })),
    [year, hasUserRightToCreateSalePlans, handleEditCell, setNewValue]
  );

  const handleOk = useCallback(async () => {
    try {
      const newValue = form.getFieldValue('newValue') || null;
      const data = {
        ...editingCell,
        PlanMoney: newValue,
      } as ModelSalePlan;

      setNewValue(Number(form.getFieldValue('newValue')));
      setModal(false);
      setEditingCell(data);

      await updateSalePlans({
        ActiveBranchId: activeBranchId,
        data: [data],
      }).unwrap();

      await updateRegistry();

      showNotification('План продаж обновлен успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      form.setFieldValue('newValue', '');
    }
  }, [
    editingCell,
    form,
    setNewValue,
    activeBranchId,
    updateSalePlans,
    updateRegistry,
    showNotification,
  ]);

  return (
    <>
      <Table
        rowKey="rowKey"
        loading={loader}
        className="plans-table"
        bordered
        columns={salePlanColumns}
        dataSource={generateSalePlanRows(data)}
        pagination={{
          position: ['none', 'none'],
          pageSize: 99999,
        }}
        style={{ marginTop: contentWidth > 390 ? '18px' : '53px' }}
        rowClassName="yearly-sales-plan"
      />

      <Modal
        title={generateSpanWithDataTestId(
          'Введите значение',
          'amount-value-title'
        )}
        data-testid="amount-value-popup"
        closeIcon={<CloseIconsForModalWithDataTestId />}
        open={isModal}
        onCancel={() => {
          form.setFieldValue('newValue', '');
          setModal(false);
        }}
        okButtonProps={{
          style: {
            display: 'none',
          },
        }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <Form
          form={form}
          name="sale-plan-editor"
          initialValues={{ remember: true }}
          autoComplete={OFF}
          onFinish={handleOk}
        >
          <Form.Item name="newValue" style={{ marginBottom: '8px' }}>
            <Input
              maxLength={15}
              onChange={(event) => {
                form.setFieldValue(
                  'newValue',
                  saveOnlyNumbers(event.target.value)
                );
              }}
              data-testid="amount-value"
            />
          </Form.Item>
          <Row justify="end">
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" data-testid="save">
                Сохранить
              </Button>
            </Form.Item>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
